/**
 * PostHog for Kronos — Ticketack global project only (no client dual-send).
 * Filter in PostHog with property app=kronos.
 * Fire-and-forget; no PII.
 *
 * Project API key (phc_) is a public client write key — same pattern as eshop/WP/POS.
 */

import { PostHog } from 'posthog-js';

const APP_NAME = 'kronos';
/** Ticketack global product analytics (hardcoded like other Ticketack apps). */
const API_KEY = 'phc_kBHqpqfwyMyc5nn56qyoekDHCFE9GrLD4fFKt8ePoYQn'; // gitleaks:allow
const API_HOST = 'https://eu.i.posthog.com';
const GENERIC_INSTANCE_LABELS = new Set([
    'www', 'engine', 'api', 'api-kronos', 'fastapi', 'kronos', 'kore', 'app', 'ticketack',
]);

let client = null;
let base_properties = {
    app: APP_NAME,
    env: process.env.NODE_ENV || 'development',
};

/**
 * Hosting prefix from engine/eshop URL (sft-engine.ticketack.com → sft).
 * Not the Kore slug, not qr_host, not the customer display name.
 */
export function instance_prefix_from_uri(uri) {
    if (!uri)
        return undefined;
    try {
        const host = new URL(String(uri)).hostname.toLowerCase().replace(/:\d+$/, '');
        const engine = host.match(/^([a-z0-9-]+)-engine\.ticketack\.com$/);
        if (engine)
            return engine[1];
        const site = host.match(/^([a-z0-9-]+)\.ticketack\.com$/);
        if (site && is_tenant_instance_label(site[1]))
            return site[1];
        const first = host.split('.')[0] || '';
        const stripped = first.replace(/[-_](engine|eshop|api|fastapi)$/i, '');
        if (is_tenant_instance_label(stripped))
            return stripped;
    } catch (_e) {
        /* ignore unparseable origins */
    }
    return undefined;
}

function is_tenant_instance_label(label) {
    if (!label)
        return false;
    if (GENERIC_INSTANCE_LABELS.has(label))
        return false;
    if (label.startsWith('api-'))
        return false;
    return true;
}

/**
 * Tenant hosting prefix from instance settings — never the shared Kore API host.
 */
export function instance_prefix_from_settings(setting) {
    if (!setting)
        return undefined;
    return instance_prefix_from_uri(setting.eshop?.uri)
        || instance_prefix_from_uri(setting.mobile?.production?.engineUri);
}

function strip_url_query(value) {
    if (typeof value !== 'string' || value.indexOf('?') === -1)
        return value;
    try {
        const u = new URL(value, typeof window !== 'undefined' ? window.location.origin : 'https://ticketack.com');
        u.search = '';
        u.hash = '';
        return u.toString();
    } catch (_e) {
        return value.split('?')[0];
    }
}

function sanitize_posthog_properties(properties) {
    if (!properties || typeof properties !== 'object')
        return properties;
    ['$current_url', '$initial_current_url', '$referrer', 'url', 'path'].forEach((key) => {
        if (properties[key])
            properties[key] = strip_url_query(properties[key]);
    });
    return properties;
}

function apply_instance_group(ph) {
    const instance = base_properties.instance;
    if (!ph || !instance)
        return;
    try {
        ph.group('instance', String(instance).slice(0, 100), {
            name: String(base_properties.customer || instance).slice(0, 100),
        });
    } catch (_e) {
        /* ignore */
    }
}

function is_local_hostname(hostname) {
    const host = String(hostname || '').toLowerCase().replace(/:\d+$/, '');
    if (!host || host === 'localhost' || host === '127.0.0.1' || host === '::1')
        return true;
    if (host.endsWith('.local') || host.endsWith('.test') || host.endsWith('.localhost'))
        return true;
    return false;
}

/**
 * Skip analytics on local/dev. Opt-in with localStorage.posthog_debug=1.
 */
export function should_enable_posthog() {
    try {
        if (typeof window !== 'undefined'
            && window.localStorage
            && window.localStorage.getItem('posthog_debug') === '1')
            return true;
    } catch (_e) {
        /* ignore */
    }
    if (process.env.NODE_ENV === 'development')
        return false;
    if (typeof window !== 'undefined' && is_local_hostname(window.location.hostname))
        return false;
    return true;
}

/**
 * Normalize marketplace dashboard_url / iframe paste into an embeddable src.
 * Project URLs (/project/…/dashboard/…) require PostHog login and cannot be iframed.
 */
export function normalize_posthog_dashboard_url(raw) {
    const input = String(raw || '').trim();
    if (!input)
        return '';
    const iframe_src = input.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    const candidate = (iframe_src ? iframe_src[1] : input).trim();
    try {
        const u = new URL(candidate);
        return u.toString();
    } catch (_e) {
        return candidate;
    }
}

export function is_posthog_embeddable_dashboard_url(url) {
    try {
        const u = new URL(url);
        return /\/(embedded|shared)\//i.test(u.pathname);
    } catch (_e) {
        return false;
    }
}

function create_client() {
    if (!API_KEY)
        return null;

    const ph = new PostHog();
    ph.init(API_KEY, {
        api_host: API_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_performance: { web_vitals: true },
        persistence: 'localStorage+cookie',
        disable_session_recording: true,
        disable_surveys: true,
        advanced_disable_feature_flags: true,
        sanitize_properties: sanitize_posthog_properties,
        loaded: (instance) => {
            try {
                instance.register({ app: APP_NAME });
                apply_instance_group(instance);
            } catch (_e) {
                /* ignore */
            }
        },
    });
    return ph;
}

function schedule_idle(fn) {
    if (typeof window === 'undefined') {
        fn();
        return;
    }
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => fn(), { timeout: 3000 });
        return;
    }
    window.setTimeout(fn, 0);
}

export function init_posthog() {
    try {
        if (!should_enable_posthog() || !API_KEY) {
            client = null;
            return;
        }
        if (client)
            return;
        // Defer off the critical path so first paint / auth is never blocked.
        schedule_idle(() => {
            try {
                if (client || !should_enable_posthog())
                    return;
                client = create_client();
                if (client && base_properties) {
                    client.register(base_properties);
                    apply_instance_group(client);
                }
            } catch (err) {
                if (process.env.NODE_ENV === 'development')
                    console.warn('[posthog] init failed', err);
                client = null;
            }
        });
    } catch (err) {
        if (process.env.NODE_ENV === 'development')
            console.warn('[posthog] init failed', err);
        client = null;
    }
}

export function set_posthog_context(props = {}) {
    const next = { ...base_properties };
    Object.keys(props || {}).forEach((key) => {
        const value = props[key];
        if (value == null || value === '')
            return;
        next[key] = String(value).slice(0, 100);
    });
    base_properties = next;
    if (!client)
        return;
    try {
        client.register(base_properties);
        apply_instance_group(client);
    } catch (_e) {
        /* ignore */
    }
}

export function identify_posthog_user(distinct_id) {
    if (!client || distinct_id == null || distinct_id === '')
        return;
    try {
        client.identify(String(distinct_id).slice(0, 100));
    } catch (_e) {
        /* ignore */
    }
}

export function reset_posthog() {
    if (!client)
        return;
    try {
        client.reset();
    } catch (_e) {
        /* ignore */
    }
}

export function track_event(event_name, properties = {}) {
    if (!client || !event_name)
        return;

    const props = { ...base_properties };
    Object.keys(properties || {}).forEach((key) => {
        const value = properties[key];
        if (value == null || value === '')
            return;
        props[key] = typeof value === 'string' ? value.slice(0, 100) : value;
    });

    try {
        client.capture(event_name, props);
    } catch (err) {
        if (process.env.NODE_ENV === 'development')
            console.warn('[posthog] capture failed', event_name, err);
    }
}

export function track_pageview(pathname) {
    track_event('$pageview', {
        path: pathname ? String(pathname).slice(0, 200) : '/',
    });
}
